import React, { useState, useEffect } from 'react';
import { FaTimes, FaSpinner, FaFileDownload } from 'react-icons/fa';
import minisiteService from '../../services/minisiteService';
import './CreateMinisiteModal.css';
import './AddPagesModal.css';

const FormSubmissionsModal = ({ minisiteId, onClose }) => {
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchSubmissions = async () => {
            try {
                const response = await minisiteService.getFormSubmissions(minisiteId);
                if (response.status) {
                    setSubmissions(response.data);
                }
            } catch (error) {
                console.error("Failed to fetch form submissions:", error);
            } finally {
                setLoading(false);
            }
        };

        if (minisiteId) {
            fetchSubmissions();
        }
    }, [minisiteId]);

    const handleDownloadCSV = () => {
        if (submissions.length === 0) return;

        // Extract all dynamic keys from the JSON data over all submissions
        const allKeys = new Set();
        submissions.forEach(sub => {
            if (sub.data) {
                Object.keys(sub.data).forEach(k => allKeys.add(k));
            }
        });

        const headers = ['Submitted At', ...Array.from(allKeys)];
        const rows = submissions.map(sub => {
            const dateStr = new Date(sub.created_at).toLocaleString();
            const row = [dateStr];

            headers.slice(1).forEach(key => {
                let val = sub.data ? sub.data[key] : '';
                if (Array.isArray(val)) {
                    val = val.join('; ');
                }
                // escape quotes for CSV
                if (typeof val === 'string') {
                    val = `"${val.replace(/"/g, '""')}"`;
                }
                row.push(val || '');
            });
            return row;
        });

        const csvContent = [
            headers.join(','),
            ...rows.map(e => e.join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `form_submissions_${minisiteId}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="ms-cm-overlay ms-fsm-overlay" onClick={onClose}>
            <div className="ms-cm-container ms-add-pages-container" onClick={e => e.stopPropagation()}>
                {/* ── Header ── */}
                <div className="ms-cm-header ms-cm-header--blue">
                    <h2 className="ms-cm-title ms-cm-title--flex">Form Submissions</h2>
                    <button type="button" className="ms-cm-close ms-cm-close--white" onClick={onClose} aria-label="Close">
                        <FaTimes size={14} />
                    </button>
                </div>

                {/* ── Body ── */}
                <div className="ms-fsm-body">
                    {loading ? (
                        <div className="ms-fsm-loading">
                            <FaSpinner className="fa-spin" size={24} color="#1d5ca3" />
                        </div>
                    ) : submissions.length === 0 ? (
                        <div className="ms-fsm-empty">
                            <p>No form submissions yet.</p>
                        </div>
                    ) : (
                        <div>
                            <div className="ms-fsm-toolbar">
                                <div className="ms-fsm-count">Total Submissions: <strong>{submissions.length}</strong></div>
                                <button onClick={handleDownloadCSV} className="ms-fsm-export-btn">
                                    <FaFileDownload /> Export CSV
                                </button>
                            </div>

                            <div className="ms-fsm-list">
                                {submissions.map(sub => (
                                    <div key={sub.id} className="ms-fsm-card">
                                        <div className="ms-fsm-date">
                                            Submitted on {new Date(sub.created_at).toLocaleString()}
                                        </div>
                                        <div className="ms-fsm-data-grid">
                                            {sub.data && Object.entries(sub.data).map(([key, val]) => (
                                                <div key={key}>
                                                    <div className="ms-fsm-data-key">{key}</div>
                                                    <div className="ms-fsm-data-val">
                                                        {Array.isArray(val) ? val.join(', ') : (val || '-')}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FormSubmissionsModal;
